import { MapItem, TwoFactorAuthService, TwoFactorMethod } from '@capturum/auth';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestroyBase } from '@capturum/shared';
import { takeUntil } from 'rxjs/operators';
import { CapturumInputCodeComponent } from '@capturum/ui/input-code';

@Component({
  selector: 'emc-multi-factor-enable-form',
  templateUrl: './multi-factor-enable-form.component.html',
  styleUrls: ['./multi-factor-enable-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MultiFactorEnableFormComponent extends DestroyBase implements OnInit {
  public TwoFactorMethod: typeof TwoFactorMethod = TwoFactorMethod;
  public types$: Observable<MapItem[]>;
  public formGroup: FormGroup;
  public paragraphNumbers: Record<string, number>;

  @Input()
  public canSelectMethod: boolean = true;

  @Input()
  public selectedMethod: TwoFactorMethod;

  @Output()
  public autoSubmit = new EventEmitter<{ code: string, method: TwoFactorMethod }>();

  @Output()
  public currentForm = new EventEmitter<{ code: string, method: TwoFactorMethod, isValid: boolean }>();

  @ViewChild(CapturumInputCodeComponent)
  private inputCodeComponent: CapturumInputCodeComponent;

  public get controlMethod(): AbstractControl {
    return this.formGroup.get('method');
  }

  public get controlCode(): AbstractControl {
    return this.formGroup.get('code');
  }

  constructor(
    protected twoFactorAuthService: TwoFactorAuthService,
    private formBuilder: FormBuilder,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.types$ = this.twoFactorAuthService.list2faMethods();
    this.formGroup = this.generateForm();

    this.updateParagraphNumbers();
    this.resetCodeWhenMethodChanges();
    this.onFormGroupChange();
  }

  public resetCodeWhenMethodChanges(): void {
    this.controlMethod.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.resetCode();
      this.updateParagraphNumbers();
    });
  }

  public appInputCodeFilled(): void {
    this.autoSubmit.emit(this.formGroup.value);
  }

  public updateParagraphNumbers(): void {
    const selectionOffset = (this.canSelectMethod) ? 1 : 0;
    const authMethodOffset = (
      this.controlMethod.value === TwoFactorMethod.google || this.selectedMethod === TwoFactorMethod.microsoft
    ) ? 2 : 1;

    this.paragraphNumbers = {
      'method': selectionOffset,
      'description': selectionOffset + 1,
      'code': selectionOffset + authMethodOffset + 1,
    };
  }

  public showAndFocusInputCode(): void {
    this.resetCode();

    Promise.resolve(null).then(() => {
      this.inputCodeComponent.focus();
    });
  }

  private resetCode(): void {
    this.controlCode.setValue(null);
  }

  private generateForm(): FormGroup {
    return this.formBuilder.group({
      code: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      method: [this.selectedMethod, Validators.required],
    });
  }

  private onFormGroupChange(): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.currentForm.emit({
        code: this.formGroup.value.code,
        method: this.formGroup.value.method,
        isValid: this.formGroup.valid,
      });
    });
  }
}
